import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Gauge, ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';

export const ProductCard = ({ product, index = 0 }) => {
  const [imageSrc, setImageSrc] = useState(product.image);
  const imageFitClass = product.imageFit === 'contain' ? 'object-contain p-5' : 'object-cover';
  const capacity = product.specifications?.Capacity || product.specifications?.['Max Capacity'] || product.specifications?.['Capacity Range'];
  const protection = product.specifications?.['Protection Class'] || product.specifications?.Protection || product.specifications?.['Controller Protection'];
  const fallbackImage = product.category === 'indicators'
    ? '/assets/hitech-combi-indicator.jpg'
    : '/assets/pitless-weighbridge.jpg';

  useEffect(() => {
    setImageSrc(product.image);
  }, [product.image]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.28, delay: index * 0.08, ease: 'easeOut' }}
      data-testid={`product-card-${product.slug}`}
    >
      <Link
        to={`/products/p/${product.slug}`}
        className="group depth-card premium-product-card block h-full overflow-hidden border border-white/10 bg-white/5 transition-all duration-300 hover:border-[#60A5FA] hover:shadow-[0_24px_60px_rgba(37,99,235,0.2)]"
      >
        <div className="relative aspect-[4/3] overflow-hidden bg-[radial-gradient(circle_at_top,rgba(96,165,250,0.14),transparent_42%),linear-gradient(180deg,#112448_0%,#0B1730_100%)]">
          <img
            src={imageSrc}
            alt={product.name}
            loading="lazy"
            className={`media-grade h-full w-full transition-transform duration-700 group-hover:scale-105 ${imageFitClass}`}
            onError={() => setImageSrc(fallbackImage)}
          />
          <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[#06101f]/90 to-transparent" />
        </div>

        <div className="flex min-h-[220px] flex-col p-6">
          <span className="text-xs font-mono uppercase tracking-widest text-[#93C5FD]">
            {product.category.replace(/-/g, ' ')}
          </span>
          <h3 className="mt-3 text-[1.85rem] font-bold leading-[1.06] text-white transition-colors group-hover:text-[#60A5FA] md:text-[2rem]">
            {product.name}
          </h3>
          <div className="mt-4 flex flex-wrap gap-2">
            {capacity && (
              <span className="inline-flex items-center gap-1.5 border border-[#60A5FA]/25 bg-[#1E40AF]/15 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-[#DBEAFE]">
                <Gauge className="h-3.5 w-3.5 text-[#93C5FD]" />
                {capacity}
              </span>
            )}
            {protection && (
              <span className="inline-flex items-center gap-1.5 border border-[#60A5FA]/25 bg-[#1E40AF]/15 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-[#DBEAFE]">
                <ShieldCheck className="h-3.5 w-3.5 text-[#93C5FD]" />
                {protection}
              </span>
            )}
          </div>
          <p className="mt-3 line-clamp-3 text-base leading-7 text-slate-300">
            {product.tagline}
          </p>

          <div className="mt-auto flex items-center gap-2 pt-6 text-sm font-semibold uppercase tracking-wider text-[#60A5FA]">
            <span>View Details</span>
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default ProductCard;
